import type { TimelineItem } from '@/shared/types'

/**
 * Which timeline entries still stand.
 *
 * A correction is an append: it names the entry it withdraws rather than
 * removing it, because the timeline is append-only — a read-append-write drops
 * concurrent entries. So every reader has to skip what has been withdrawn, and
 * one that forgets reports events that did not happen. That is how a retracted
 * booking kept counting in the meetings tile, and how a retracted call kept
 * counting as an attempt in the lead's own summary strip.
 */
export function retractionsById(
  timeline: readonly TimelineItem[],
): Map<string, TimelineItem> {
  const out = new Map<string, TimelineItem>()
  for (const it of timeline) {
    if (it.kind !== 'correction') continue
    if (it.correctsId) out.set(it.correctsId, it)
  }
  return out
}

/** Ids of entries a correction has withdrawn. */
export function retractedIds(timeline: readonly TimelineItem[]): Set<string> {
  return new Set(retractionsById(timeline).keys())
}

/**
 * The timeline as it stands: withdrawn entries and the corrections that
 * withdrew them both gone.
 *
 * For counting and summarising. The timeline *view* deliberately renders both,
 * struck through, so the history stays auditable — it must not use this.
 */
export function liveEntries(timeline: readonly TimelineItem[]): TimelineItem[] {
  const gone = retractedIds(timeline)
  return timeline.filter(
    it => it.kind !== 'correction' && !(it.id && gone.has(it.id)),
  )
}
